import React from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import PsychologyIcon from '@mui/icons-material/Psychology';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ApiIcon from '@mui/icons-material/Api';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useI18n } from '@/i18n/I18nContext';

const HomePage: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useI18n();

	return (
		<>
			<Box
				sx={{
					py: { xs: 8, md: 12 },
					textAlign: 'center',
					color: 'common.white',
					background: 'linear-gradient(135deg, #FF9800 0%, #FF6F00 50%, #E65100 100%)'
				}}
			>
				<Container maxWidth="lg">
					<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
						<Typography variant="h2" fontWeight={800} sx={{ mb: 2, textTransform: 'uppercase' }}>
							{t.home.title}
						</Typography>
						<Typography variant="h6" sx={{ maxWidth: 900, mx: 'auto', opacity: 0.95 }}>
							{t.home.subtitle}
						</Typography>
						<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 4 }}>
							<Button variant="contained" color="secondary" size="large" onClick={() => navigate('/batches')}>
								{t.home.viewBatches}
							</Button>
							<Button variant="outlined" color="inherit" size="large" onClick={() => navigate('/ai-assistant')}>
								{t.home.tryAI}
							</Button>
						</Stack>
					</motion.div>
				</Container>
			</Box>

			<Container maxWidth="lg" sx={{ py: 8 }}>
				<Grid container spacing={3} alignItems="stretch">
					<Grid item xs={12} md={4}>
						<Paper variant="outlined" sx={{ p: 3, height: '100%', borderRadius: 3 }}>
							<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
								<ApiIcon color="primary" />
								<Typography variant="h5" fontWeight={800}>
									{t.home.brewfatherIntegration}
								</Typography>
							</Stack>
							<Typography color="text.secondary">
								{t.home.brewfatherDescription}
							</Typography>
						</Paper>
					</Grid>
					<Grid item xs={12} md={4}>
						<Paper variant="outlined" sx={{ p: 3, height: '100%', borderRadius: 3 }}>
							<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
								<PsychologyIcon color="secondary" />
								<Typography variant="h5" fontWeight={800}>
									{t.home.aiAnalysis}
								</Typography>
							</Stack>
							<Typography color="text.secondary">
								{t.home.aiDescription}
							</Typography>
						</Paper>
					</Grid>
					<Grid item xs={12} md={4}>
						<Paper variant="outlined" sx={{ p: 3, height: '100%', borderRadius: 3 }}>
							<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
								<AutoAwesomeIcon color="success" />
								<Typography variant="h5" fontWeight={800}>
									{t.home.recommendations}
								</Typography>
							</Stack>
							<Typography color="text.secondary">
								{t.home.recommendationsDescription}
							</Typography>
						</Paper>
					</Grid>
				</Grid>
			</Container>

			<Container maxWidth="lg" sx={{ pt: 4, pb: 2 }}>
				<Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
					{t.home.features}
				</Typography>
			</Container>

			<Container maxWidth="lg" sx={{ pb: 10 }}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4}>
						<Card>
							<CardContent>
								<Stack spacing={1}>
									<LocalBarIcon color="primary" />
									<Typography variant="h6" fontWeight={700}>
										{t.home.batchManagement}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{t.home.batchManagementDescription}
									</Typography>
								</Stack>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Card>
							<CardContent>
								<Stack spacing={1}>
									<MenuBookIcon color="primary" />
									<Typography variant="h6" fontWeight={700}>
										{t.home.recipeLibrary}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{t.home.recipeLibraryDescription}
									</Typography>
								</Stack>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Card>
							<CardContent>
								<Stack spacing={1}>
									<PsychologyIcon color="primary" />
									<Typography variant="h6" fontWeight={700}>
										{t.home.aiAssistant}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{t.home.aiAssistantDescription}
									</Typography>
								</Stack>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Container>

			<Container maxWidth="md" sx={{ pb: 8 }}>
				<Typography variant="h5" fontWeight={800} sx={{ mb: 2 }}>
					{t.home.howItWorks}
				</Typography>
				<Stack spacing={1.5} sx={{ textAlign: 'left' }}>
					<Stack direction="row" spacing={1.5} alignItems="flex-start">
						<CheckCircleOutline color="success" />
						<Typography>{t.home.step1}</Typography>
					</Stack>
					<Stack direction="row" spacing={1.5} alignItems="flex-start">
						<CheckCircleOutline color="success" />
						<Typography>{t.home.step2}</Typography>
					</Stack>
					<Stack direction="row" spacing={1.5} alignItems="flex-start">
						<CheckCircleOutline color="success" />
						<Typography>{t.home.step3}</Typography>
					</Stack>
				</Stack>
			</Container>

			<Container maxWidth="md" sx={{ pb: 10, textAlign: 'center' }}>
				<Paper
					sx={{
						p: { xs: 4, md: 6 },
						borderRadius: 4,
						background: 'linear-gradient(135deg, #FF9800 0%, #FF6F00 100%)',
						color: 'common.white'
					}}
					elevation={0}
				>
					<Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
						{t.home.getStarted}
					</Typography>
					<Typography variant="h6" sx={{ mb: 1 }}>
						{t.home.createFreeAccount}
					</Typography>
					<Typography variant="body1" sx={{ mb: 3, opacity: 0.95 }}>
						🎉 Get 15 free AI credits to try out our tool!
					</Typography>
					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
						<Button variant="contained" size="large" color="secondary" onClick={() => navigate('/login')}>
							{t.home.createAccount}
						</Button>
						<Button variant="outlined" size="large" color="inherit" onClick={() => navigate('/batches')}>
							{t.home.viewBatches}
						</Button>
					</Stack>
				</Paper>
			</Container>
		</>
	);
};

export default HomePage;
